export const name = "CORR";
export const summary = `Computes the Pearson coefficient of correlation of a set of number pairs.`;
export const reference = `
## CORR

Computes the Pearson coefficient of correlation of a set of number pairs.

\`\`\`
CORR(
  X1, X2
)
[OVER over_clause]

over_clause:
  { named_window | ( [window_specification] ) }

window_specification:
  [named_window]
  [PARTITION BY partition_expression [, ...] ]
  [ORDER BY expression [ { ASC | DESC }] [, ...] ]
  [window_frame_clause]
\`\`\`

### Description

Returns the Pearson coefficientof correlation of a set of number pairs. For each number pair, the first number is the dependent variable and the second number is the independent variable. The return result is between \`-1\` and \`1\`. A result of \`0\` indicates no correlation.

All numeric types are supported. If the input is \`NUMERIC\` or \`BIGNUMERIC\` then the internal aggregation is stable with the final output converted to a \`FLOAT64\`. Otherwise the input is converted to a \`FLOAT64\`before aggregation, resulting in a potentially unstable result.

This function ignores any input pairs that contain one or more \`NULL\` values. If there are fewer than two input pairs without \`NULL\` values, this function returns \`NULL\`.

\`NaN\` is produced if:

- Any input value is \`NaN\`
- Any input value is positive infinity or negative infinity.
- The variance of \`X1\` or \`X2\` is \`0\`.
- The covariance of \`X1\` and \`X2\` is \`0\`.

To learn more about the optional aggregate clauses that you can pass into this function, see Aggregate function calls.

To learn more about the \`OVER\` clause and how to use it, see Window function calls.


### Return Data Type

\`FLOAT64\`


### Examples



\`\`\`
SELECT CORR(y, x) AS results
FROM
  UNNEST(
    [
      STRUCT(1.0 AS y, 5.0 AS x),
      (3.0, 9.0),
      (4.0, 7.0)]);

/*--------------------*
 | results |
 +--------------------+
 | 0.6546536707079772 |
 *--------------------*/
\`\`\`



\`\`\`
SELECT CORR(y, x) AS results
FROM
  UNNEST(
    [
      STRUCT(1.0 AS y, 5.0 AS x),
      (3.0, 9.0),
      (4.0, NULL)]);

/*---------*
 | results |
 +---------+
 | 1 |
 *---------*/
\`\`\`



\`\`\`
SELECT CORR(y, x) AS results
FROM UNNEST([STRUCT(1.0 AS y, NULL AS x),(9.0, 3.0)])

/*---------*
 | results |
 +---------+
 | NULL |
 *---------*/
\`\`\`



\`\`\`
SELECT CORR(y, x) AS results
FROM UNNEST([STRUCT(1.0 AS y, NULL AS x),(9.0, NULL)])

/*---------*
 | results |
 +---------+
 | NULL |
 *---------*/
\`\`\`



\`\`\`
SELECT CORR(y, x) AS results
FROM
  UNNEST(
    [
      STRUCT(1.0 AS y, 5.0 AS x),
      (3.0, 9.0),
      (4.0, 7.0),
      (5.0, 1.0),
      (7.0, CAST('Infinity' as FLOAT64))])

/*---------*
 | results |
 +---------+
 | NaN |
 *---------*/
\`\`\`



\`\`\`
SELECT CORR(x, y) AS results
FROM
  (
    SELECT 0 AS x, 0 AS y
    UNION ALL
    SELECT 0 AS x, 0 AS y
  )

/*---------*
 | results |
 +---------+
 | NaN |
 *---------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/statistical_aggregate_functions
`;
